(() => {
  const LOCK_CLASS = "elo-collection-sort-open";
  const MOBILE = window.matchMedia("(max-width: 989px)");

  class EloCollection extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === "true") return;
      this.dataset.ready = "true";

      this.grid = this.querySelector("[data-elo-collection-grid]");
      this.sortTrigger = this.querySelector("[data-elo-sort-open]");
      this.sortLayer = this.querySelector("[data-elo-sort-layer]");
      this.lastFocused = null;
      this.sortTimer = 0;

      this.onKeydown = this.onKeydown.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);

      this.sortTrigger?.addEventListener("click", () => this.setSort(true));
      this.querySelectorAll("[data-elo-sort-close]").forEach((button) => {
        button.addEventListener("click", () => this.setSort(false));
      });
      this.querySelectorAll("[data-elo-sort-value]").forEach((button) => {
        button.addEventListener("click", () =>
          this.selectSort(button.dataset.eloSortValue),
        );
      });
      this.querySelectorAll("[data-elo-grid-columns]").forEach((button) => {
        button.addEventListener("click", () =>
          this.setColumns(Number(button.dataset.eloGridColumns), true),
        );
      });

      if (typeof MOBILE.addEventListener === "function") {
        MOBILE.addEventListener("change", this.onViewportChange);
      } else {
        MOBILE.addListener(this.onViewportChange);
      }

      this.restoreColumns();
      this.observeReveals();
      requestAnimationFrame(() => this.classList.add("is-ready"));
    }

    disconnectedCallback() {
      document.removeEventListener("keydown", this.onKeydown);
      if (typeof MOBILE.removeEventListener === "function") {
        MOBILE.removeEventListener("change", this.onViewportChange);
      } else {
        MOBILE.removeListener(this.onViewportChange);
      }
      this.releasePage();
      this.revealObserver?.disconnect();
    }

    onViewportChange() {
      if (this.classList.contains("is-sort-open")) this.setSort(false, false);
      this.restoreColumns();
    }

    restoreColumns() {
      if (!this.grid) return;
      const fallback = MOBILE.matches ? 2 : 3;
      const key = MOBILE.matches
        ? "eloCollectionColumnsMobile"
        : "eloCollectionColumnsDesktop";
      let stored = 0;
      try {
        stored = Number(window.localStorage.getItem(key));
      } catch (_error) {
        stored = 0;
      }
      const allowed = MOBILE.matches ? [1, 2] : [2, 3];
      this.setColumns(allowed.includes(stored) ? stored : fallback, false);
    }

    setColumns(columns, persist = true) {
      if (!this.grid) return;
      const allowed = MOBILE.matches ? [1, 2] : [2, 3];
      const next = allowed.includes(columns) ? columns : MOBILE.matches ? 2 : 3;
      this.grid.dataset.columns = String(next);

      this.querySelectorAll("[data-elo-grid-columns]").forEach((button) => {
        const active = Number(button.dataset.eloGridColumns) === next;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", String(active));
      });

      if (persist) {
        const key = MOBILE.matches
          ? "eloCollectionColumnsMobile"
          : "eloCollectionColumnsDesktop";
        try {
          window.localStorage.setItem(key, String(next));
        } catch (_error) {
          // The grid still works when storage is unavailable.
        }
      }
    }

    setSort(open, restoreFocus = true) {
      if (!this.sortLayer || !this.sortTrigger) return;
      window.clearTimeout(this.sortTimer);

      if (open) {
        this.lastFocused = document.activeElement;
        this.sortLayer.removeAttribute("inert");
        this.sortLayer.setAttribute("aria-hidden", "false");
        this.classList.add("is-sort-open");
        this.sortTrigger.setAttribute("aria-expanded", "true");
        document.addEventListener("keydown", this.onKeydown);
        this.lockPage();
        requestAnimationFrame(() => {
          this.sortLayer
            .querySelector(
              "[data-elo-sort-value].is-active, [data-elo-sort-value]",
            )
            ?.focus({ preventScroll: true });
        });
        return;
      }

      this.classList.remove("is-sort-open");
      this.sortTrigger.setAttribute("aria-expanded", "false");
      this.sortLayer.setAttribute("aria-hidden", "true");
      document.removeEventListener("keydown", this.onKeydown);
      this.releasePage();

      this.sortTimer = window.setTimeout(
        () => {
          this.sortLayer.setAttribute("inert", "");
        },
        MOBILE.matches ? 720 : 460,
      );

      if (restoreFocus && this.lastFocused instanceof HTMLElement) {
        this.lastFocused.focus({ preventScroll: true });
      }
    }

    selectSort(value) {
      if (!value) return;
      const url = new URL(window.location.href);
      url.searchParams.set("sort_by", value);
      url.searchParams.delete("page");
      this.setSort(false, false);
      window.location.assign(url.toString());
    }

    onKeydown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        this.setSort(false);
        return;
      }

      if (event.key !== "Tab" || !this.classList.contains("is-sort-open"))
        return;
      const focusable = [
        ...this.sortLayer.querySelectorAll(
          "button:not([disabled]), a[href], input:not([disabled])",
        ),
      ].filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    lockPage() {
      if (!MOBILE.matches) return;
      document.documentElement.classList.add(LOCK_CLASS);
      document.body.classList.add(LOCK_CLASS);
    }

    releasePage() {
      document.documentElement.classList.remove(LOCK_CLASS);
      document.body.classList.remove(LOCK_CLASS);
    }

    observeReveals() {
      const items = this.querySelectorAll(
        "[data-elo-collection-reveal], [data-elo-collection-card]",
      );
      if (!("IntersectionObserver" in window)) {
        items.forEach((item) => item.classList.add("is-visible"));
        return;
      }

      this.revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: "0px 0px -6%", threshold: 0.08 },
      );
      items.forEach((item) => this.revealObserver.observe(item));
    }
  }

  if (!customElements.get("elo-collection")) {
    customElements.define("elo-collection", EloCollection);
  }
})();
